import Vue from 'vue';

import { flashcardStatsByDay } from '@/utils/flashcards';

const defaultState = () => {
  return {
    flashcardStates: {},
    flashcardStats: {},
    lastUpdatedAt: {},
    questionsAnsweredToday: 0,
  };
};

const state = defaultState();

const getters = {
  flashcardStates: state => state.flashcardStates,
  flashcardStateList: state => Object.keys(state.flashcardStates).map(id => state.flashcardStates[id]),
  flashcardStats: state => state.flashcardStats,
  lastUpdatedAt: state => state.lastUpdatedAt,
  questionsAnsweredToday: state => state.questionsAnsweredToday,
};

const mutations = {
  setFlashcardStates(state, flashcardStates) {
    for(let flashcardState of flashcardStates) {
      let id;
      if (flashcardState.categoryId) {
        id = `${flashcardState.flashcardId}-category-${flashcardState.categoryId}`;
      } else if (flashcardState.assetAssociationId) {
        id = `${flashcardState.flashcardId}-asset-association-${flashcardState.assetAssociationId}`;
      }

      if (id) {
        // if we have a flashcard state that was made before the one we have locally, ignore it
        if (state.flashcardStates[id] && state.flashcardStates[id].mobileCreatedAt > flashcardState.mobileCreatedAt) {
          continue;
        }
        // ignore states that have been reset (lastResetAt >= mobileCreatedAt)
        // flashcard states are reset when quizzes and certifications are reset
        if (flashcardState.lastResetAt) {
          Vue.delete(state.flashcardStates, id);
        } else {
          Vue.set(state.flashcardStates, id, flashcardState);
        }
      }
    }
  },
  deleteFlashcardStates(state, { type, categoryId, assetAssociationIds }) {
    let ids = [];
    if (type === 'Category') {
      ids = Object.keys(state.flashcardStates)
        .map(id => state.flashcardStates[id])
        .filter(fs => fs.categoryId == categoryId)
        .map(fs => `${fs.flashcardId}-category-${categoryId}`);
    } else if (type === 'Asset Association') {
      assetAssociationIds.forEach(assetAssociationId => {
        const flashcardState = Object.keys(state.flashcardStates)
          .map(id => state.flashcardStates[id])
          .find(fs => fs.assetAssociationId == assetAssociationId);
        if (flashcardState) {
          ids.push(`${flashcardState.flashcardId}-asset-association-${assetAssociationId}`);
        }
      });
    }
    ids.forEach(id => {
      Vue.delete(state.flashcardStates, id);
    });
  },
  setQuestionsAnsweredToday(state, total) {
    state.questionsAnsweredToday = total;
  },
  setFlashcardStats(state, flashcardStats) {
    state.flashcardStats = flashcardStats;
    if (flashcardStats.flashcard) {
      state.questionsAnsweredToday = flashcardStatsByDay(flashcardStats);
    }
  },
  setLastUpdatedAt(state, type) {
    Vue.set(state.lastUpdatedAt, type, Date.now());
  },
  reset(state) {
    Object.assign(state, defaultState());
  },
};

const actions = {
  incrementDailyQuestions({ commit, getters }) {
    const newValue = getters.questionsAnsweredToday + 1;
    commit('setQuestionsAnsweredToday', newValue);
    return newValue;
  },
  fetchStates({ dispatch, commit, getters, rootGetters }, types) {
    const params = {};

    types.forEach(type => {
      params[type] = getters.lastUpdatedAt[type] || 0;
      Vue.set(state.lastUpdatedAt, type, Date.now());
    });

    return rootGetters['session/ajax'].get('/states', { params }).then(response => {
      commit('setFlashcardStates', response.data.flashcards || []);
      commit('assetAssociationStates/set', response.data.assetAssociations || [], { root: true });
      commit('attachmentStates/set', response.data.attachments || [], { root: true });
      commit('bookmarks/setBookmarks', response.data.bookmarks || [], { root: true });
      commit('categoryStates/setCategoryStates', response.data.categories || [], { root: true });
      commit('learningModuleStates/set', response.data.learningModules || [], { root: true });
      commit('mnemonicStates/setMnemonicStates', response.data.mnemonics || [], { root: true });
      commit('topicStates/set', response.data.topics || [], {root: true});
      if (response.data.stats) {
        commit('setFlashcardStats', response.data.stats || {});
      }
      return response;
    });
  },
  send({ dispatch, commit, getters, rootGetters }, params) {
    commit('setFlashcardStates', [params]);
    return rootGetters['session/ajax'].post('/flashcard_states', params)
      .then(response => {
        if (response.data.userCertification) {
          commit('userCertifications/setUserCertifications', [response.data.userCertification], { root: true });
        }
        return response;
      })
      .catch(error => {
        console.log('Flashcard state send error:', error);
        // If it's a network error or server is unreachable
        if (!error.response || error.code === 'ECONNABORTED' || error.code === 'ECONNREFUSED') {
          console.log('Connection error detected, setting connection error state');
          commit('ui/setConnectionError', true, { root: true });
        }
        throw error;
      });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
